import type { AgentQuestion, AgentQuestionOption, QueryResult, SessionListItem, SessionSummary } from './colimind-types';

export const NUMERIC_TYPES = ['DOUBLE', 'FLOAT', 'LONG', 'INTEGER', 'INT'];

export function isTimeCol(c: QueryResult['columns'][number]): boolean {
  if (c.type_name === 'TIMESTAMP' || c.type_name === 'DATE') return true;
  const n = c.name.toLowerCase();
  return (
    n.includes('timestamp') ||
    n.includes('datetime') ||
    n.endsWith('_at') ||
    n.endsWith('_utc') ||
    n.endsWith('_time') ||
    n.endsWith('_date') ||
    n === 'date' ||
    n === 'day' ||
    n === 'week' ||
    n === 'month' ||
    n === 'year' ||
    n === 'time' ||
    n === 'dt' ||
    n === 'period'
  );
}

export function isNumericType(c: QueryResult['columns'][number]): boolean {
  return NUMERIC_TYPES.includes(c.type_name);
}

export function isCategory(c: QueryResult['columns'][number]): boolean {
  return c.type_name === 'STRING' && !isTimeCol(c);
}

export function isKpiCol(name: string): boolean {
  return ['cpi', 'chi', 'lpi', 'tpi'].some(k => name.toLowerCase().includes(k));
}

export function formatDateLabel(raw: string): string {
  const d = new Date(raw);
  return isNaN(d.getTime())
    ? raw
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
export function formatMessageTime(value: unknown): string {
  const d = new Date(value as string);
  return value == null || isNaN(d.getTime()) ? '' : d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * One-line excerpt of `text` around the first occurrence of `query`, padded by `radius`
 * characters on each side and marked with ellipses where content was cut off.
 * Returns the leading excerpt when the query does not occur.
 */
export function buildSnippet(text: string, query: string, radius = 40): string {
  const flat = (text ?? '').replace(/\s+/g, ' ').trim();
  const idx = flat.toLowerCase().indexOf(query.toLowerCase());
  const start = Math.max(0, (idx < 0 ? 0 : idx) - radius);
  const end = Math.min(flat.length, (idx < 0 ? 0 : idx + query.length) + radius);
  return (start > 0 ? '…' : '') + flat.slice(start, end).trim() + (end < flat.length ? '…' : '');
}

/**
 * Sessions matching `query` by title or by the content of any message. Content matches carry a
 * snippet so the user sees why a session matched. An empty query matches everything.
 */
export function matchSessions(sessions: SessionSummary[], query: string): SessionListItem[] {
  const q = query.toLowerCase().trim();
  if (!q) return sessions;

  const matches: SessionListItem[] = [];
  for (const session of sessions) {
    if (session.title?.toLowerCase().includes(q)) {
      matches.push(session);
      continue;
    }
    const hit = findMessageText(session, q);
    if (hit) matches.push({ ...session, snippet: buildSnippet(hit, q) });
  }
  return matches;
}

/**
 * Text of the first message matching `query`, or null. A streamed answer accumulates in
 * `text`, while a non-streamed one only ever reaches `response.answer_text` -- searching just
 * one of them would miss answers the user can plainly see on screen.
 */
function findMessageText(session: SessionSummary, query: string): string | null {
  for (const message of session.session_detail?.messages ?? []) {
    for (const candidate of [message.text, message.response?.answer_text]) {
      if (candidate?.toLowerCase().includes(query)) return candidate;
    }
  }
  return null;
}

/**
 * First name for the greeting, derived from the JWT subject (usually an e-mail address).
 * `sascha.korth@basf.com` becomes `Sascha`.
 *
 * Deliberately conservative, because names are not all Western:
 * - only the part before `@` is used, split on the separators common in e-mail local parts
 * - the first character is upper-cased with `toLocaleUpperCase()`, so scripts with special
 *   casing rules (Turkish dotted/dotless i) follow the runtime locale, and scripts without
 *   case (Chinese, Japanese, Arabic) are left untouched
 * - the remainder is only lower-cased when the whole token is upper case, so `SASCHA` becomes
 *   `Sascha` while `McDonald` keeps its inner capital
 * - iteration is per code point, so names outside the BMP are not cut in half
 */
export function firstNameFromSubject(subject: string): string {
  const local = (subject ?? '').split('@')[0];
  const token = local.split(/[._\-\s+]+/).find(part => part.length > 0) ?? '';
  if (!token) return '';

  const chars = [...token];
  const head = chars[0].toLocaleUpperCase();
  const tail = chars.slice(1).join('');
  const isShouting = token === token.toLocaleUpperCase() && token !== token.toLocaleLowerCase();
  return head + (isShouting ? tail.toLocaleLowerCase() : tail);
}

/**
 * Reads the `ask_user` questions from a `response.approval_required` event.
 * Returns an empty list for approvals of other tools.
 */
export function parseAgentQuestions(raw: unknown): AgentQuestion[] {
  const requests = (raw as { action_requests?: { args?: { questions?: unknown } }[] })?.action_requests;
  if (!Array.isArray(requests)) return [];

  return requests
    .flatMap(r => Array.isArray(r?.args?.questions) ? r.args.questions : [])
    .filter(q => typeof q?.question === 'string' && q.question.trim())
    .map(q => ({
      question: q.question,
      ...(typeof q.message === 'string' && q.message.trim() ? { message: q.message.trim() } : {}),
      options: (Array.isArray(q.options) ? q.options : [])
        .map((o: { value?: string; label?: string; description?: string }) => ({
          value: o?.value ?? o?.label ?? '',
          label: o?.label ?? o?.value ?? '',
          description: o?.description,
        }))
        .filter((o: AgentQuestionOption) => o.label),
      multiSelect: q.multi_select === true,
      allowFreeform: q.allow_freeform_input === true,
      required: q.required !== false,
    }));
}

/** One line per answered question, with the question first so the agent can match the answer. */
export function formatAgentAnswers(questions: AgentQuestion[], answers: string[]): string {
  return questions
    .map((q, i) => answers[i]?.trim() ? `${q.question} ${answers[i].trim()}` : '')
    .filter(Boolean)
    .join('\n');
}

export function toolStatusKey(name: string | undefined): string {
  return name === 'aladdin' ? 'colimind.status.queryingData' : 'colimind.status.usingTool';
}

/** True when the key goes into a text field, so it must not count as a shortcut. */
export function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  return Boolean(element?.isContentEditable) || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element?.tagName ?? '');
}
